/**
 * Aldo Shoes - Product Card Interactions
 * Handles Quick Add functionality for collection grids.
 * Quick View is handled by the quick-view-modal snippet.
 */

// Mirror of the Liquid palette in snippets/swatches.liquid — keep in sync.
const ALDO_SWATCH_PALETTE = {
  black: '#000000',
  white: '#FFFFFF',
  ivory: '#FFFFF0',
  cream: '#FFFDD0',
  beige: '#F5F5DC',
  bone: '#E3DAC9',
  ecru: '#C2B280',
  tan: '#D2B48C',
  camel: '#C19A6B',
  brown: '#964B00',
  chocolate: '#7B3F00',
  cognac: '#9A463D',
  burgundy: '#800020',
  bordeaux: '#5C0120',
  maroon: '#800000',
  red: '#CC0000',
  coral: '#FF7F50',
  orange: '#FF6600',
  yellow: '#FFD700',
  gold: '#D4AF37',
  'rose gold': '#B76E79',
  pink: '#FFC0CB',
  blush: '#DE5D83',
  magenta: '#CA1F7B',
  purple: '#800080',
  lilac: '#C8A2C8',
  navy: '#000080',
  blue: '#0000FF',
  cobalt: '#0047AB',
  denim: '#1560BD',
  teal: '#008080',
  green: '#008000',
  olive: '#808000',
  khaki: '#C3B091',
  grey: '#808080',
  gray: '#808080',
  silver: '#C0C0C0',
  charcoal: '#36454F',
  slate: '#708090',
  'black patent': '#111111',
};

function aldoColorHexFromHandle(handle) {
  if (!handle) return '';
  const parts = String(handle).toLowerCase().split('-');
  if (parts.length < 2) return '';
  for (let len = Math.min(3, parts.length - 1); len >= 1; len--) {
    const candidate = parts.slice(1, 1 + len).join(' ');
    if (Object.prototype.hasOwnProperty.call(ALDO_SWATCH_PALETTE, candidate)) {
      return ALDO_SWATCH_PALETTE[candidate];
    }
  }
  return '';
}

function aldoColorNameFromHandle(handle) {
  if (!handle) return '';
  const parts = String(handle).toLowerCase().split('-');
  if (parts.length < 2) return '';
  for (let len = Math.min(3, parts.length - 1); len >= 1; len--) {
    const candidate = parts.slice(1, 1 + len).join(' ');
    if (Object.prototype.hasOwnProperty.call(ALDO_SWATCH_PALETTE, candidate)) {
      return candidate;
    }
  }
  return '';
}

// Tiny dot thumbnail from a Shopify CDN photo URL (width=96). The CDN honors
// the width param, so dots never download full-size images.
function aldoThumb(src) {
  if (!src || src.indexOf('cdn.shopify.com') === -1) return src || '';
  return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=96';
}

class ProductCardManager {
  constructor() {
    this.init();
    this.loadSiblingLists();
  }

  /**
   * Sibling model swatches. Siblings = products of the same MODEL (same
   * `title`, our catalog's model name), resolved client-side from the full
   * `/products.json?limit=250` catalog. Environment-proof: no admin filter, no
   * `?section=` — works on the local dev server and the live storefront.
   *
   * Residual fallback only: rows the server already rendered carry
   * `data-siblings-complete` and are skipped (instant, no fetch, no pop-in).
   * Order is handle-sorted (FIXED, same as server) and dots prefer the
   * product photo over a letter initial.
   */
  loadSiblingLists() {
    const containers = Array.from(document.querySelectorAll('[data-card-siblings]'))
      .filter((c) => !c.hasAttribute('data-siblings-complete'))
      .filter((c) => !c.querySelector('.product-card__swatch--sibling:not(.product-card__swatch--current)'));
    if (!containers.length) return;
    this.loadCatalog().then(() => {
      containers.forEach((c) => {
        const handle = c.dataset.productHandle || '';
        const title = c.dataset.productTitle || '';
        const dots = this.resolveSiblings(handle, title);
        if (!dots.length) return;
        let currentDot = '';
        if (!c.querySelector('.product-card__swatch--current')) {
          const hex = aldoColorHexFromHandle(handle);
          const colorName = aldoColorNameFromHandle(handle) || title;
          const currentImg = this.productPhotoForHandle(handle);
          const dataImg = currentImg ? ` data-image-url="${currentImg}"` : '';
          const thumb = currentImg ? aldoThumb(currentImg) : '';
          if (hex) {
            currentDot = `<span class="product-card__swatch product-card__swatch--sibling product-card__swatch--current is-active"${dataImg} title="${title} (${colorName})" style="background-color: ${hex};"></span>`;
          } else if (thumb) {
            currentDot = `<span class="product-card__swatch product-card__swatch--sibling product-card__swatch--current is-active"${dataImg} title="${title}" style="background-image: url('${thumb}'); background-size: cover; background-position: center;"></span>`;
          } else {
            currentDot = `<span class="product-card__swatch product-card__swatch--sibling product-card__swatch--current is-active"${dataImg} title="${title}" style="background-color: var(--color-surface-secondary);">${String(handle || title || 'S')[0].toUpperCase()}</span>`;
          }
        }
        c.insertAdjacentHTML('beforeend', currentDot + dots);
      });
    });
  }

  loadCatalog() {
    if (window.aldoSiblingsCatalog) return Promise.resolve(window.aldoSiblingsCatalog);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const pages = Array.from({ length: 8 }, (_, i) => i + 1);
    return Promise.all(pages.map((page) =>
      fetch(`${root}products.json?limit=250&page=${page}`)
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((data) => (Array.isArray(data.products) ? data.products : []))
    ))
      .then((all) => {
        const merged = [].concat(...all);
        window.aldoSiblingsCatalog = merged;
        return merged;
      })
      .catch(() => {
        window.aldoSiblingsCatalog = window.aldoSiblingsCatalog || [];
        return window.aldoSiblingsCatalog;
      });
  }

  resolveSiblings(handle, title) {
    const key = (title || '').trim().toLowerCase();
    const catalog = window.aldoSiblingsCatalog || [];
    // FIXED order: handle-sorted, same as the server spans. Cap with +N.
    const siblings = catalog
      .filter((p) => p.handle !== handle && (p.title || '').trim().toLowerCase() === key)
      .sort((a, b) => String(a.handle || '').localeCompare(String(b.handle || '')));
    const LIMIT = 4;
    const shown = siblings.slice(0, LIMIT);
    const extra = siblings.length - shown.length;
    const dots = shown.map((p) => {
      const hex = aldoColorHexFromHandle(p.handle);
      const colorName = aldoColorNameFromHandle(p.handle) || p.title || '';
      const safeTitle = String(p.title || p.handle || '').replace(/"/g, '&quot;');
      const img = this.productPhotoForHandle(p.handle);
      const dataImg = img ? ` data-image-url="${String(img).replace(/"/g, '&quot;')}"` : '';
      const thumb = img ? aldoThumb(img) : '';
      if (hex) {
        return `<input type="radio" name="swatch-sibling-${handle}" id="sibling-${handle}-${p.handle}" class="product-card__swatch-input" value="${p.handle}"><label for="sibling-${handle}-${p.handle}" class="product-card__swatch product-card__swatch--sibling"${dataImg} title="${safeTitle} (${colorName})" style="background-color: ${hex};"></label>`;
      }
      if (thumb) {
        return `<input type="radio" name="swatch-sibling-${handle}" id="sibling-${handle}-${p.handle}" class="product-card__swatch-input" value="${p.handle}"><label for="sibling-${handle}-${p.handle}" class="product-card__swatch product-card__swatch--sibling"${dataImg} title="${safeTitle}" style="background-image: url('${thumb}'); background-size: cover; background-position: center;"></label>`;
      }
      return `<input type="radio" name="swatch-sibling-${handle}" id="sibling-${handle}-${p.handle}" class="product-card__swatch-input" value="${p.handle}"><label for="sibling-${handle}-${p.handle}" class="product-card__swatch product-card__swatch--sibling"${dataImg} title="${safeTitle}" style="background-color: var(--color-surface-secondary);">${String(p.handle || 'S')[0].toUpperCase()}</label>`;
    }).join('');
    if (extra > 0) {
      return dots + `<a href="/products/${String(handle).replace(/"/g, '')}" class="product-card__swatch-more">+${extra}</a>`;
    }
    return dots;
  }

  productPhotoForHandle(handle) {
    const catalog = window.aldoSiblingsCatalog || [];
    const p = catalog.find((x) => x.handle === handle);
    if (!p) return '';
    return (p.featured_image && p.featured_image.src) || (p.images && p.images[0] && p.images[0].src) || '';
  }

  init() {
    document.addEventListener('click', (e) => {
      const quickAddBtn = e.target.closest('[data-quick-add]');
      if (quickAddBtn) {
        e.preventDefault();
        this.handleQuickAdd(quickAddBtn);
        return;
      }
    });

    // Warm each colour's photos the first time the pointer reaches the card, so
    // the swap on click is a paint rather than a download. Runs once per card.
    document.addEventListener('mouseover', (e) => {
      const card = e.target.closest?.('.product-card, .search-item');
      if (!card || card.dataset.swatchImagesPrefetched) return;
      const urls = [...card.querySelectorAll('.product-card__swatch[data-image-url]')]
        .flatMap((s) => [s.dataset.imageUrl, s.dataset.hoverImageUrl])
        .filter(Boolean);
      if (urls.length === 0) return;
      card.dataset.swatchImagesPrefetched = 'true';
      urls.forEach((url) => {
        const img = new Image();
        img.src = url;
      });
    });

    document.addEventListener('click', (e) => {
      // Cualquier swatch responde al click, no solo los que traen foto o URL
      // de sibling — antes, un color sin foto propia (variant_img vacío en
      // swatches.liquid) no tenía data-image-url NI data-product-url, así que
      // este selector no lo encontraba nunca: el radio de abajo se marcaba
      // por el <label for> nativo del navegador, pero como este handler
      // retornaba antes del preventDefault/is-active, el punto no se veía
      // seleccionado y no pasaba nada — leía como "no deja hacerle click".
      const swatch = e.target.closest('.product-card__swatch');
      if (!swatch) return;
      e.preventDefault();

      const card = swatch.closest('.product-card, .search-item');
      if (!card) return;

      const imageUrl = swatch.dataset.imageUrl;
      const productUrl = swatch.dataset.productUrl;
      const primaryImg = card.querySelector(
        '.product-card__image--primary img, .product-card__image, .search-item__img'
      );

      if (imageUrl && primaryImg) {
        // Swap only once the new photo is decoded. The old version faded the
        // card to opacity 0 and swapped 300ms later, which left the wrapper's
        // grey background exposed — it read as a loading skeleton and made
        // changing colour feel slow. Preloading keeps the current photo on
        // screen until the next one can be painted in the same frame.
        const preloader = new Image();
        preloader.onload = () => {
          primaryImg.src = imageUrl;
          primaryImg.srcset = '';
        };
        preloader.src = imageUrl;
      }

      // The hover photo belongs to a colour too — without this it keeps showing
      // the previously selected colour's second shot.
      const hoverImageUrl = swatch.dataset.hoverImageUrl;
      const secondaryImg = card.querySelector('.product-card__image--secondary');
      if (hoverImageUrl && secondaryImg) {
        secondaryImg.src = hoverImageUrl;
        secondaryImg.srcset = '';
      }

      // Every color is a separate product (not a Shopify variant), so
      // "selecting" a swatch means retargeting the card's own links —
      // image, title, quick-add — at that sibling's real product URL.
      if (productUrl) {
        card.querySelectorAll('.product-card__link, .product-card__title a').forEach((link) => {
          link.setAttribute('href', productUrl);
        });
        const quickAdd = card.querySelector('[data-quick-view], [data-quick-add]');
        if (quickAdd && swatch.dataset.productHandle) {
          quickAdd.dataset.productHandle = swatch.dataset.productHandle;
        }
      }

      card.querySelectorAll('.product-card__swatch.is-active').forEach((s) => s.classList.remove('is-active'));
      swatch.classList.add('is-active');

      // Active state is styled by TWO independent rules (critical.css): the
      // `.is-active` class above and `input:checked + label`. The
      // preventDefault() at the top of this handler cancels the label's native
      // radio activation, so without syncing the radio here the previously
      // checked one stays lit and TWO dots look selected at once.
      const radio = swatch.control || (swatch.htmlFor ? document.getElementById(swatch.htmlFor) : null);
      if (radio && radio.type === 'radio') radio.checked = true;

      // Precio/oferta son POR COLOR (swatches.liquid manda data-price /
      // data-compare-price / data-savings del color de este swatch).
      const priceCurrentEl = card.querySelector('[data-price-current]');
      const priceCompareEl = card.querySelector('[data-price-compare]');
      const badgeSaleEl = card.querySelector('[data-badge-sale]');
      const swatchPrice = swatch.dataset.price;
      const swatchIsSale = Boolean(swatch.dataset.comparePrice);
      if (priceCurrentEl && swatchPrice) {
        priceCurrentEl.textContent = swatchPrice;
        priceCurrentEl.classList.toggle('is-sale', swatchIsSale);
      }
      if (priceCompareEl) {
        priceCompareEl.hidden = !swatchIsSale;
        if (swatchIsSale) priceCompareEl.textContent = swatch.dataset.comparePrice;
      }
      if (badgeSaleEl) {
        badgeSaleEl.hidden = !swatchIsSale;
      }
    });
  }

  async handleQuickAdd(button) {
    const variantId = button.dataset.productId;
    const productTitle = button.dataset.productTitle;

    if (!variantId) return;

    // Colombia: variante trackeada con stock bajo no se agrega directo,
    // se abre WhatsApp para consultar disponibilidad.
    if (button.dataset.consult === 'true') {
      const href = button.dataset.waHref;
      if (href) window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }

    button.classList.add('is-loading');
    button.disabled = true;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const msg = data.description || data.message || 'Failed to add item to cart';
        throw new Error(msg);
      }

      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      document.dispatchEvent(new CustomEvent('cart:open'));

    } catch (error) {
      const msg = error.message || 'Error';
      if (window.cartToast) {
        window.cartToast.show(msg, 'error', { duration: 3000 });
      }
      console.error('Quick Add Error:', error);
      button.classList.add('is-error');
      const originalText = button.textContent;
      button.textContent = button.dataset.errorText || 'Error';
      setTimeout(() => {
        button.classList.remove('is-error');
        button.textContent = originalText;
      }, 2000);
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductCardManager());
} else {
  new ProductCardManager();
}
